import type { GetStaticPaths, GetStaticProps } from 'next'
import type { SSRConfig } from 'next-i18next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useNextSanityImage } from 'next-sanity-image'
import PreviewProvider from 'next-sanity/preview'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import type { ImageLoaderProps } from 'next/image'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { tv } from 'tailwind-variants'

import { supportedMarketSchema } from '@nxt-insurance/constants'
import Spinner from '@nxt-insurance/ui-kit/components/Spinner'
import Columns from '@nxt-insurance/ui-kit/components/layoutComponents/Columns'
import Stack from '@nxt-insurance/ui-kit/components/layoutComponents/Stack'
import WaveSeparator from '@nxt-insurance/ui-kit/components/layoutComponents/WaveSeparator'
import { SCREEN_SIZE } from '@nxt-insurance/ui-kit/constants'
import { body1, body2, h1 } from '@nxt-insurance/ui-kit/core/typography'

import type { BlogPost as BlogPostType } from '@/cms/blog'
import { blogPostsQuery, postQuery } from '@/cms/blog'
import { client, generateSrcSet, runQuery, sanityClientLike, urlFor } from '@/cms/client'
import type { SiteSettingsNavbar } from '@/cms/pages'
import { navbarQuery, siteSettingsQueryNavbar } from '@/cms/pages'
import { Icon } from '@/components/Icon'
import { PageMetaDescription } from '@/components/PageMetaDescription'
import { BlockContent } from '@/components/renderers/BlockContent'
import { Footer } from '@/components/sections/Footer'
import Header from '@/components/sections/Header'
import { APP_ENV } from '@/config/env'
import { BLOG_CONTENT_WIDTH } from '@/constants/content'
import { useAlternateLinks } from '@/hooks/useAlternateLinks'
import { useAppendCurrentQueryParametersToAnchorTags } from '@/hooks/useAppendQueryParametersToAnchorTags'
import { fetchTreatment } from '@/services/splitTreatments'
import { getLocaleParts } from '@/utils/locale'
import { selectNavbarVariation } from '@/utils/navbar'
import { getPageUrl } from '@/utils/url'
import { useQuery } from '@tanstack/react-query'

const AUTHOR_IMAGE_HEIGHT = 40
const SEPARATOR_SYMBOL = '•'

type Props = SSRConfig & {
  preview: boolean
  data: {
    post: BlogPostType
    navbar: SiteSettingsNavbar
  }
}

const DynamicNotFoundSection = dynamic(
  async () => import('@/components/sections/NotFoundSection'),
  {
    loading: () => <Spinner />,
    ssr: false,
  }
)

const shareIconLinkStyle = tv({
  base: 'flex items-center justify-center no-underline',
})

const socialIconsStyle = tv({
  base: 'h-auto w-full',
})

const blogPostStyles = tv({
  slots: {
    blogHeaderContainer:
      'mx-auto flex max-w-3xl flex-col px-4 pb-4 pt-8 text-left lg:pb-6 lg:pt-24',
    contentContainer: 'flex w-full max-w-3xl flex-col p-6 lg:mx-auto lg:mb-10 lg:px-0 lg:py-6',
    postDetailsAndSharingWrapper: 'relative flex max-w-full flex-col lg:pt-8',
    sharedIconsContainer: 'absolute -right-24 top-24 z-[1] hidden grid-flow-row gap-y-6 lg:grid',
  },
})

const BlogPostContent = (props: { post: BlogPostType }) => {
  const { t } = useTranslation()
  const { asPath, locale } = useRouter()
  const { fixed, flexible, ...imageProps } = useMemo(() => {
    const url = urlFor(props.post.heroImage.image.asset)
    return {
      fixed: generateSrcSet(url.width(SCREEN_SIZE.XL)),
      flexible: generateSrcSet(url),
      loader: (options: ImageLoaderProps) => options.src,
      src: url.width(SCREEN_SIZE.XL).url(),
      width: SCREEN_SIZE.XL,
      height: SCREEN_SIZE.XL / props.post.heroImage.image.asset.metadata.dimensions.aspectRatio,
      alt: props.post.heroImage.alt || '',
    }
  }, [props.post.heroImage])
  const authorImageProps = useNextSanityImage(
    sanityClientLike,
    props.post.author?.image.asset ?? null,
    {
      imageBuilder: (imageUrlBuilder) =>
        imageUrlBuilder
          .height(AUTHOR_IMAGE_HEIGHT)
          .width(
            Math.round(
              AUTHOR_IMAGE_HEIGHT *
                (props.post.author?.image.asset?.metadata.dimensions.aspectRatio ?? 0)
            )
          ),
    }
  )
  const {
    blogHeaderContainer,
    contentContainer,
    postDetailsAndSharingWrapper,
    sharedIconsContainer,
  } = blogPostStyles()

  return (
    <div className="flex w-full flex-col">
      <div className={blogHeaderContainer()}>
        <Stack space="small">
          <h1 className={h1()} id="CY_detailPage_title">
            {props.post.heading}
          </h1>
          <p className={body1()}>{props.post.description}</p>
          <Columns className="items-center" space="xsmall">
            <Columns className="items-center" space="xxsmall">
              <div>
                <Icon
                  size="small"
                  icon={{
                    name: 'article_24',
                    bgColor: 'playground1Light',
                    bgShape: 'A',
                  }}
                />
              </div>
              <span className={body2({ emphasis: 'medium' })}>
                {t(`blog.articleDetails.publicationType.${props.post.publicationType}`)}
              </span>
            </Columns>
            {props.post.publicationType === 'blogPost' ? (
              <>
                <span className={body2({ emphasis: 'medium' })}>{SEPARATOR_SYMBOL}</span>
                <span className={body2({ emphasis: 'medium' })}>
                  {t('blog.articleDetails.minuteReadText', {
                    readingTime: props.post.readingTime,
                  })}
                </span>
              </>
            ) : null}
            <span className={body2({ emphasis: 'medium' })}>{SEPARATOR_SYMBOL}</span>
            <span className={body2({ emphasis: 'medium' })}>{props.post.publishDate}</span>
          </Columns>
        </Stack>
      </div>
      <picture className="ml-auto mr-auto flex w-full flex-row items-center justify-center md:px-4">
        <source srcSet={fixed} media={`(min-width: ${SCREEN_SIZE.M}px)`} />
        <source srcSet={flexible} />
        <Image {...imageProps} className="m-w-7xl h-auto w-full" alt="Blog image" />
      </picture>
      <div className={contentContainer()}>
        <div className={postDetailsAndSharingWrapper()}>
          <div className={sharedIconsContainer()}>
            <Link
              className={shareIconLinkStyle()}
              href={`https://www.facebook.com/sharer.php?u=${getPageUrl(asPath, locale)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                className={socialIconsStyle()}
                src="/assets/images/social/Facebook.svg"
                alt="Facebook icon"
                height={40}
                width={40}
                loader={({ src }) => src}
                unoptimized
              />
            </Link>
            <Link
              className={shareIconLinkStyle()}
              href={`https://twitter.com/intent/tweet?url=${getPageUrl(asPath, locale)}&text=${
                props.post.description
              }`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                className={socialIconsStyle()}
                src="/assets/images/social/Twitter.svg"
                alt="Twitter Icon"
                height={40}
                width={40}
                loader={({ src }) => src}
                unoptimized
              />
            </Link>
            <Link
              className={shareIconLinkStyle()}
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${getPageUrl(
                asPath,
                locale
              )}&text=${props.post.description}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                className={socialIconsStyle()}
                src="/assets/images/social/LinkedIn.svg"
                alt="LinkedIn Icon"
                height={40}
                width={40}
                loader={({ src }) => src}
                unoptimized
              />
            </Link>
          </div>
        </div>

        <div className="flex w-full flex-col">
          <BlockContent
            blocks={props.post.content}
            width={BLOG_CONTENT_WIDTH}
            sectionName="blogPost"
            align="left"
          />
        </div>
      </div>
      <div className={contentContainer()}>
        <WaveSeparator color="uiGreyDark" />
        {props.post.publicationType === 'blogPost' && authorImageProps && props.post.author && (
          <Columns className="mt-8 items-center" space="small">
            <div>
              <Image {...authorImageProps} alt={props.post.author.firstName} />
            </div>
            <div>
              {t('blog.articleDetails.author', {
                authorFirstName: props.post.author.firstName,
              })}
            </div>
          </Columns>
        )}
      </div>
    </div>
  )
}

const BlogPost = (props: Props) => {
  const { locale, query, isPreview } = useRouter()
  const [lang, market] = getLocaleParts(locale)

  // Error handling is handled on the server this query is always pre-rendered
  // on the client
  // https://tanstack.com/query/v4/docs/react/guides/ssr
  const { data: post, isFetching } = useQuery({
    queryKey: ['blogPost', lang, market, query.slug],
    queryFn: async () =>
      runQuery(postQuery, {
        lang,
        market,
        slug: query.slug?.[0] ?? null,
      }),
    initialData: props.data?.post,
    throwOnError: true,
  })
  const { data: siteSettingsNavbar } = useQuery({
    queryFn: async (): Promise<SiteSettingsNavbar | undefined> => {
      const res = await runQuery(siteSettingsQueryNavbar, { lang, market })
      return res?.navbar
    },
    queryKey: ['siteSettingsNavbar', lang, market],
    initialData: props.data?.navbar,
    throwOnError: true,
  })

  const { data: navbar } = useQuery({
    queryFn: async () => {
      if (siteSettingsNavbar?._type === 'website.navbar.ab.test') {
        const previewTreatment =
          query[siteSettingsNavbar.featureFlag] ??
          siteSettingsNavbar.variations[
            Math.floor(Math.random() * siteSettingsNavbar.variations.length)
          ].name
        const splitTreatment = await fetchTreatment(siteSettingsNavbar.featureFlag, { lang })
        const treatment = previewTreatment && isPreview ? previewTreatment : splitTreatment
        const selectedVariation = selectNavbarVariation(
          siteSettingsNavbar,
          typeof treatment === 'string' ? treatment : undefined
        )
        if (!selectedVariation) {
          throw new Error(
            `Assertion: no treatment matched ${
              previewTreatment ?? splitTreatment
            } for the experiment ${siteSettingsNavbar.title}`
          )
        }
        return runQuery(navbarQuery, { lang, id: selectedVariation.navbar._id })
      }
      return runQuery(navbarQuery, { lang, id: siteSettingsNavbar?._id })
    },
    queryKey: ['navbar', lang, market],
    enabled: Boolean(siteSettingsNavbar),
    throwOnError: true,
  })

  const postMarkets = useMemo(() => {
    return post?.availableMarkets.map((m) => {
      const parsed = supportedMarketSchema.safeParse(m)

      if (parsed.success) {
        return parsed.data
      }
      return market
    })
  }, [post, market])

  const slugs = useMemo(() => {
    if (!post) {
      return {}
    }
    const convertedSlugs: Record<string, { current: string } | undefined> = {}
    Object.entries(post.slugs).forEach(([l, s]) => {
      if (!s) {
        convertedSlugs[l] = s
        return
      }
      convertedSlugs[l] = { current: `posts/${s.current}` }
    })
    return convertedSlugs
  }, [post])

  const alternateLinks = useAlternateLinks(slugs, postMarkets)
  useAppendCurrentQueryParametersToAnchorTags()
  if (!post) {
    if (isFetching) {
      return <Spinner fullScreen />
    }
    return (
      <>
        <Head>
          {alternateLinks.map(({ href, hrefLang }) => (
            <link rel="alternate" href={href} hrefLang={hrefLang} key={hrefLang} />
          ))}
        </Head>
        <PageMetaDescription />
        <Header pageType="not_found" navbar={navbar} alternateLinks={alternateLinks} />
        <DynamicNotFoundSection />
        <Footer />
      </>
    )
  }
  if (!props.preview) {
    return (
      <>
        <Head>
          {alternateLinks.map(({ href, hrefLang }) => (
            <link rel="alternate" href={href} hrefLang={hrefLang} key={hrefLang} />
          ))}
        </Head>
        <PageMetaDescription metaDescription={post.metaDescription} />
        <Header pageType="regular" navbar={navbar} alternateLinks={alternateLinks} />
        <BlogPostContent post={post} />
        <Footer />
      </>
    )
  }
  return (
    <PreviewProvider client={client}>
      <Head>
        {alternateLinks.map(({ href, hrefLang }) => (
          <link rel="alternate" href={href} hrefLang={hrefLang} key={hrefLang} />
        ))}
      </Head>
      <PageMetaDescription metaDescription={post.metaDescription} />
      <Header pageType="regular" navbar={navbar} alternateLinks={alternateLinks} />
      <BlogPostContent post={post} />
      <Footer />
    </PreviewProvider>
  )
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  // Better to disable it in development, otherwise it feels incredibly slow
  if (!locales || process.env.NODE_ENV === 'development') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const filteredLocales = locales.filter((locale) => locale !== 'default')

  const paths: { params: Record<string, string | string[]>; locale: string }[] = []
  for (const locale of filteredLocales) {
    const [lang, market] = getLocaleParts(locale)
    const pages = await runQuery(blogPostsQuery, {
      market,
      lang,
    })
    const filteredPosts = pages.filter((page) => page.slug !== null)
    for (const post of filteredPosts) {
      paths.push({
        params: {
          slug: [post.slug],
        },
        locale,
      })
    }
  }

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const slugFromContext = ctx.params?.slug ?? ['']
  const slug = Array.isArray(slugFromContext) ? slugFromContext.join('/') : slugFromContext
  const locale =
    ctx.locale === 'default' || ctx.locale === undefined
      ? (ctx.defaultLocale as string)
      : ctx.locale
  if (locale === 'default') {
    return {
      notFound: true,
    }
  }
  if (!locale) {
    throw new Error('Assertion: missing locale')
  }

  const [lang, market] = getLocaleParts(locale)

  const [post, siteSettingsNavbar] = await Promise.all([
    runQuery(postQuery.nullable(), {
      lang,
      market,
      slug: slug,
    }),
    runQuery(siteSettingsQueryNavbar, { lang, market }),
  ])

  if (!post) {
    return {
      notFound: true,
    }
  }

  if (APP_ENV === 'production' && !ctx.preview && !post.enabledInProduction) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      preview: false,
      data: {
        navbar: siteSettingsNavbar.navbar,
        post,
      },
    },
  }
}

export default BlogPost
